import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertCircle } from 'lucide-react';
import { HOME_ROUTE } from '@/utils/const';

// Types
type OSInfo = {
  os: string;
  osVersion: string;
};

type DebugInfo = {
  ip: string;
  browser: string;
  language: string;
  timezone: string;
  currentTime: string;
  javascript: string;
  screenResolution: string;
  os: string;
  osVersion: string;
};

// Constants
const OS_PATTERNS = [
  { pattern: /Windows NT (\d+\.\d+)/, os: 'Windows', version: (match: RegExpMatchArray) => match[1] },
  { pattern: /Mac OS X (\d+[._]\d+)/, os: 'macOS', version: (match: RegExpMatchArray) => match[1].replace('_', '.') },
  { pattern: /Linux\s+([^\s]+)/, os: 'Linux', version: (match: RegExpMatchArray) => match[1] },
  { pattern: /Android\s+([^\s;]+)/, os: 'Android', version: (match: RegExpMatchArray) => match[1] },
  { pattern: /OS\s+(\d+_\d+)/, os: 'iOS', version: (match: RegExpMatchArray) => match[1].replace('_', '.') },
] as const;

const DEFAULT_DEBUG_INFO: DebugInfo = {
  ip: 'Не удалось определить',
  browser: navigator.userAgent,
  language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currentTime: new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) + ' MSK',
  javascript: 'Да',
  screenResolution: `${window.innerWidth}x${window.innerHeight}`,
  os: 'Неизвестно',
  osVersion: '',
};

// Utility functions
const getOSInfo = (userAgent: string): OSInfo => {
  for (const { pattern, os, version } of OS_PATTERNS) {
    const match = userAgent.match(pattern);
    if (match) return { os, osVersion: version(match) };
  }
  return { os: 'Неизвестно', osVersion: '' };
};

const fetchDebugInfo = async (): Promise<DebugInfo> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const { ip } = await response.json();
    const userAgent = navigator.userAgent;
    const { os, osVersion } = getOSInfo(userAgent);

    return {
      ip,
      browser: userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currentTime: new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) + ' MSK',
      javascript: 'Да',
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      os,
      osVersion,
    };
  } catch (error) {
    console.error('Failed to fetch debug info:', error);
    return DEFAULT_DEBUG_INFO;
  }
};

// Components
const DebugInfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[140px,1fr] gap-2">
    <div className="text-sm font-medium text-gray-500">{label}:</div>
    <div className="text-sm text-gray-900 break-all">{value}</div>
  </div>
);

const DebugInfoDisplay = ({ info }: { info: DebugInfo }) => (
  <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
    {Object.entries(info).map(([key, value]) => (
      <DebugInfoRow
        key={key}
        label={
          key === 'os' ? 'Операционная система' :
          key === 'osVersion' ? 'Версия' :
          key.charAt(0).toUpperCase() + key.slice(1)
        }
        value={key === 'os' ? `${info.os}${info.osVersion ? ` ${info.osVersion}` : ''}` : value}
      />
    ))}
  </div>
);

const ErrorHeader = () => (
  <>
    <div className="mb-6 flex items-center justify-center">
      <div className="rounded-full bg-red-100 p-3">
        <AlertCircle className="h-6 w-6 text-red-600" />
      </div>
    </div>
    <h1 className="mb-2 text-center text-xl font-semibold text-gray-900">
      К сожалению, произошла ошибка в приложении
    </h1>
    <p className="mb-6 text-center text-gray-600">
      Мы уже работаем над устранением проблемы. Пожалуйста, попробуйте позже.
    </p>
  </>
);

const DebugDialog = ({
  isOpen,
  onClose,
  debugInfo,
  onCopy,
  isCopied
}: {
  isOpen: boolean;
  onClose: () => void;
  debugInfo: DebugInfo | null;
  onCopy: () => void;
  isCopied: boolean;
}) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-[500px] flex flex-col max-h-[90vh]">
      <DialogHeader>
        <DialogTitle>Отладочная информация</DialogTitle>
        <DialogDescription>
          Эта информация может помочь в диагностике проблемы
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4 overflow-y-auto flex-1">
        {debugInfo && <DebugInfoDisplay info={debugInfo} />}
      </div>
      <DialogFooter className="flex-col sm:flex-row sm:justify-end mt-4">
        <Button
          onClick={onCopy}
          variant="secondary"
          className="w-full sm:w-auto sm:mr-2 mb-2 sm:mb-0 min-w-[100px]"
        >
          {isCopied ? 'Скопировано!' : 'Копировать'}
        </Button>
        <Button onClick={onClose} className="w-full sm:w-auto">
          Закрыть
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export function FallbackComponent() {
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  useEffect(() => {
    fetchDebugInfo().then(setDebugInfo);
  }, []);

  const handleCopyDebugInfo = async () => {
    if (!debugInfo) return;

    const debugText = Object.entries(debugInfo)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(debugText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-[600px] px-4">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <ErrorHeader />
          <div className="flex justify-center flex-col gap-2">
            <Button
              onClick={() => setShowDebugInfo(true)}
              variant="outline"
              className="border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Детальная информация
            </Button>
            <Button
              onClick={() => (window.location.href = HOME_ROUTE)}
              variant="link"
              className="border-gray-200 text-gray-700 hover:bg-gray-50 ml-2"
            >
              На главную
            </Button>
          </div>
        </div>

        <DebugDialog
          isOpen={showDebugInfo}
          onClose={() => setShowDebugInfo(false)}
          debugInfo={debugInfo}
          onCopy={handleCopyDebugInfo}
          isCopied={copied}
        />
      </div>
    </div>
  );
}
